import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Card,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  ArrowBack,
  QrCode2,
  TrendingUp,
  TrendingDown,
  Coffee,
  CalendarToday,
  Visibility
} from '@mui/icons-material';
import axios from 'axios';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

interface CafeQRStats {
  cafeId: string;
  cafeName: string;
  totalScans: number;
  last7Days: number;
  last30Days: number;
}

interface QRSummary {
  totalScans: number;
  todayScans: number;
  totalCafesWithScans: number;
  topCafes: CafeQRStats[];
  scansByCafe: CafeQRStats[];
}

const QRStatisticsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<QRSummary | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState<'total' | 'last7Days' | 'last30Days'>('total');

  useEffect(() => {
    fetchQRStats();
  }, []);

  const fetchQRStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      const response = await axios.get('https://api.sobcof.com/api/qr-stats/summary', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('QR istatistikleri alınırken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSortedCafes = () => {
    if (!stats) return [];

    const cafes = [...stats.scansByCafe];

    switch (sortBy) {
      case 'last7Days':
        return cafes.sort((a, b) => b.last7Days - a.last7Days);
      case 'last30Days':
        return cafes.sort((a, b) => b.last30Days - a.last30Days);
      default:
        return cafes.sort((a, b) => b.totalScans - a.totalScans);
    }
  };

  const getTrendIcon = (cafe: CafeQRStats) => {
    if (cafe.last7Days > cafe.last30Days / 4) {
      return <TrendingUp sx={{ color: '#4caf50', fontSize: 20 }} />;
    } else if (cafe.last7Days < cafe.last30Days / 6) {
      return <TrendingDown sx={{ color: '#f44336', fontSize: 20 }} />;
    }
    return null;
  };

  // Top 10 kafeler için bar chart verisi
  const chartData = stats?.topCafes.slice(0, 10).map(cafe => ({
    name: cafe.cafeName.length > 15 ? cafe.cafeName.substring(0, 15) + '...' : cafe.cafeName,
    tarama: cafe.totalScans
  })) || [];

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f8fafc'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: '#8B4513', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            İstatistikler yükleniyor...
          </Typography>
        </Box>
      </Box>
    );
  }

  const sortedCafes = getSortedCafes();
  const paginatedCafes = sortedCafes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc' }}>
      {/* App Bar */}
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            mr: 2,
            width: 40,
            height: 40
          }}>
            <QrCode2 sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h5" component="div" sx={{
            flexGrow: 1,
            fontWeight: 600,
            color: 'white'
          }}>
            QR Kod İstatistikleri
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Summary Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{
              p: 3,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
              color: 'white'
            }}>
              <QrCode2 sx={{ fontSize: 50, mb: 2 }} />
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {stats?.totalScans || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Toplam Tarama
              </Typography>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{
              p: 3,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #D2691E 0%, #CD853F 100%)',
              color: 'white'
            }}>
              <CalendarToday sx={{ fontSize: 50, mb: 2 }} />
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {stats?.todayScans || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Bugünkü Tarama
              </Typography>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{
              p: 3,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #CD853F 0%, #DEB887 100%)',
              color: 'white'
            }}>
              <Coffee sx={{ fontSize: 50, mb: 2 }} />
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {stats?.totalCafesWithScans || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Aktif Kafe
              </Typography>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{
              p: 3,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #DEB887 0%, #F4A460 100%)',
              color: 'white'
            }}>
              <TrendingUp sx={{ fontSize: 50, mb: 2 }} />
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {stats?.topCafes[0]?.totalScans || 0}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                En Yüksek Tarama
              </Typography>
            </Card>
          </Box>
        </Box>

        {/* Chart */}
        {chartData.length > 0 && (
          <Card elevation={0} sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            border: '1px solid #e2e8f0'
          }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              En Çok Taranan 10 Kafe
            </Typography>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="name"
                  stroke="#64748b"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                />
                <YAxis stroke="#64748b" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                />
                <Bar
                  dataKey="tarama"
                  fill="url(#colorGradient)"
                  radius={[8, 8, 0, 0]}
                />
                <defs>
                  <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8B4513" />
                    <stop offset="100%" stopColor="#D2691E" />
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Filter and Table */}
        <Card elevation={0} sx={{
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          overflow: 'hidden'
        }}>
          <Box sx={{
            p: 3,
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Kafe Bazında Detaylar
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Sıralama</InputLabel>
              <Select
                value={sortBy}
                label="Sıralama"
                onChange={(e) => setSortBy(e.target.value as any)}
              >
                <MenuItem value="total">Toplam Tarama</MenuItem>
                <MenuItem value="last7Days">Son 7 Gün</MenuItem>
                <MenuItem value="last30Days">Son 30 Gün</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Sıra</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Kafe Adı</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Toplam Tarama</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Son 7 Gün</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Son 30 Gün</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Trend</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Detay</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCafes.map((cafe, index) => (
                  <TableRow
                    key={cafe.cafeId}
                    sx={{
                      '&:hover': { backgroundColor: '#f8fafc' },
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={page * rowsPerPage + index + 1}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: index < 3 ? 'linear-gradient(135deg, #00c9ff 0%, #92fe9d 100%)' : 'default'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{
                          bgcolor: '#8B4513',
                          width: 35,
                          height: 35
                        }}>
                          <Coffee fontSize="small" />
                        </Avatar>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {cafe.cafeName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={cafe.totalScans}
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {cafe.last7Days}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {cafe.last30Days}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getTrendIcon(cafe)}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/admin/partner-cafes`)}
                        title="Detayları Görüntüle"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={sortedCafes.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Sayfa başına satır:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            sx={{
              borderTop: '1px solid #e2e8f0'
            }}
          />
        </Card>
      </Box>
    </Box>
  );
};

export default QRStatisticsPage;
